import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import BlogCard from '../components/BlogCard';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { getPublishedBlogs, getClientSettings } from '../../firebase/blogService';
import { formatDate } from '../../shared/utils';
import '../user.css';

const CLIENT_ID = process.env.REACT_APP_CLIENT_ID;

// Skeleton loader
const CardSkeleton = () => (
  <div className="u-card" style={{ pointerEvents: 'none' }}>
    <div className="u-skeleton" style={{ aspectRatio: '16/9' }} />
    <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="u-skeleton" style={{ height: 16, width: '30%', borderRadius: 20 }} />
      <div className="u-skeleton" style={{ height: 20, width: '90%' }} />
      <div className="u-skeleton" style={{ height: 20, width: '75%' }} />
      <div className="u-skeleton" style={{ height: 14, width: '60%', marginTop: 4 }} />
    </div>
  </div>
);

export default function BlogListPage() {
  const [blogs, setBlogs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';

  useEffect(() => {
    if (!CLIENT_ID) {
      setLoading(false);
      return;
    }
    (async () => {
      const [posts, s] = await Promise.all([
        getPublishedBlogs(CLIENT_ID),
        getClientSettings(CLIENT_ID),
      ]);
      setBlogs(posts);
      setSettings(s);
      // Apply primary color from settings
      if (s?.primaryColor) {
        document.documentElement.style.setProperty('--client-primary', s.primaryColor);
      }
      setLoading(false);
    })();
  }, []);

  const categories = useMemo(() => {
    const cats = [...new Set(blogs.map((b) => b.category).filter(Boolean))];
    return ['All', ...cats];
  }, [blogs]);

  const displayedBlogs = useMemo(() => {
    let list = blogs;
    if (activeCategory !== 'All') {
      list = list.filter((b) => b.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (b) =>
          b.title?.toLowerCase().includes(q) ||
          b.excerpt?.toLowerCase().includes(q) ||
          b.tags?.some((t) => t.toLowerCase().includes(q))
      );
    }
    return list;
  }, [blogs, activeCategory, searchQuery]);

  const featured = blogs.find((b) => b.featured);
  const heroPost = featured || blogs[0];
  const hasFilteredView = Boolean(searchQuery) || activeCategory !== 'All';
  const sectionBlogs = hasFilteredView
    ? displayedBlogs
    : displayedBlogs.filter((b) => b.id !== heroPost?.id);
  const shouldShowLatestSection = loading || hasFilteredView || displayedBlogs.length === 0 || sectionBlogs.length > 0;

  if (!CLIENT_ID) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'Inter, sans-serif' }}>
        <div style={{ textAlign: 'center', maxWidth: 480 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚙️</div>
          <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 10 }}>Client ID Not Configured</h2>
          <p style={{ color: '#64748b', marginBottom: 20 }}>
            Set <code style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 4 }}>REACT_APP_CLIENT_ID</code> in your <code style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 4 }}>.env</code> file to load blog content.
          </p>
          <p style={{ fontSize: 13, color: '#94a3b8' }}>
            You can find your Client ID in the admin panel under <strong>Settings</strong>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar />

      <main style={{ flex: 1, paddingTop: 96 }}>
        {/* Hero featured post */}
        {!loading && heroPost && !searchQuery && activeCategory === 'All' && (
          <div className="u-container" style={{ paddingTop: 28 }}>
            <Link to={`/blog/${heroPost.slug}`} className="u-hero">
              {heroPost.coverImage ? (
                <img src={heroPost.coverImage} alt={heroPost.title} className="u-hero-image" />
              ) : (
                <div className="u-no-cover" style={{ aspectRatio: '21/9' }} />
              )}
              <div className="u-hero-gradient" />
              <div className="u-hero-content">
                {heroPost.category && (
                  <span className="u-hero-category">{heroPost.category}</span>
                )}
                <h1 className="u-hero-title">{heroPost.title}</h1>
                <div className="u-hero-meta">
                  <span>{heroPost.authorName || 'Author'}</span>
                  <span className="u-hero-meta-dot" />
                  <span>{formatDate(heroPost.publishedAt)}</span>
                  {heroPost.readTime && (
                    <>
                      <span className="u-hero-meta-dot" />
                      <span>{heroPost.readTime} min read</span>
                    </>
                  )}
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Blog grid section */}
        {shouldShowLatestSection && (
        <div className="u-container" style={{ paddingTop: 40, paddingBottom: 60 }}>
          {/* Category tabs (only when not searching) */}
          {!searchQuery && categories.length > 1 && (
            <div className="u-cat-tabs">
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={`u-cat-tab ${activeCategory === cat ? 'active' : ''}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Section header */}
          <div className="u-section-header">
            <h2 className="u-section-title">
              <span />
              {searchQuery
                ? `Results for "${searchQuery}"`
                : activeCategory === 'All'
                ? 'Latest Articles'
                : activeCategory}
            </h2>
            {!loading && (
              <span style={{ fontSize: 13, color: '#94a3b8' }}>
                {sectionBlogs.length} {sectionBlogs.length === 1 ? 'article' : 'articles'}
              </span>
            )}
          </div>

          {/* Loading skeletons */}
          {loading && (
            <div className="u-blog-grid">
              {[...Array(6)].map((_, i) => <CardSkeleton key={i} />)}
            </div>
          )}

          {/* Empty state */}
          {!loading && sectionBlogs.length === 0 && (
            <div className="u-empty">
              <div className="u-empty-icon">{searchQuery ? '🔍' : '📄'}</div>
              <div className="u-empty-title">
                {searchQuery ? 'No articles found' : 'No articles yet'}
              </div>
              <div className="u-empty-text">
                {searchQuery
                  ? `No results for "${searchQuery}". Try a different search term.`
                  : 'Check back soon for new content.'}
              </div>
            </div>
          )}

          {/* Grid */}
          {!loading && sectionBlogs.length > 0 && (
            <>
              {/* Featured card (large) */}
              {!searchQuery && activeCategory === 'All' && sectionBlogs[0] && (
                <div style={{ marginBottom: 28 }}>
                  <BlogCard post={sectionBlogs[0]} featured />
                </div>
              )}

              {/* Regular grid */}
              <div className="u-blog-grid">
                {((!searchQuery && activeCategory === 'All') ? sectionBlogs.slice(1) : sectionBlogs).map((post) => (
                  <BlogCard key={post.id} post={post} />
                ))}
              </div>
            </>
          )}
        </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
